import { makeObservable, observable, computed, action, toJS } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'

import { Comments } from '../comments'

/**
 * Class representing a Subtask.
 */
/**
 * Subtask represents a single unit of work within a larger item.
 *
 * @class Subtask
 * @param {Object} params - Initialization parameters.
 * @param {string} [params.id] - Unique identifier. Generated if absent.
 * @param {string} params.name - Display name of the subtask.
 * @param {Array<string>} [params.images] - Filenames of attached images.
 * @param {string} params.description - Detailed description.
 * @param {string} params.status - Current status (e.g., 'active', 'completed').
 * @param {string} [params.web] - Optional related URL.
 * @param {number} [params.sortId=0] - Ordering index for display.
 * @param {string} params.createdBy - User ID who created this subtask.
 * @param {number} [params.createdTs] - Unix timestamp of creation. Defaults to now.
 * @param {Array<Object>} [params.comments] - Initial comment data.
 */
export class Subtask {
	id
	name
	description
	images
	status
	sortId
	createdTs
	createdBy
	comments
	web

	constructor({
		id,
		name,
		images,
		description,
		status,
		web,
		sortId,
		createdBy,
		createdTs,
		comments = []
	}) {
		this.id = id || uuidv4()
		this.name = name
		this.images = images || []
		this.description = description
		this.status = status
		this.web = web || ''
		this.sortId = sortId || 0
		this.createdBy = createdBy
		this.createdTs = createdTs || dayjs().unix()
		this.comments = new Comments(toJS(comments))

		makeObservable(this, {
			name: observable,
			images: observable,
			description: observable,
			status: observable,
			sortId: observable,
			createdTs: observable,
			createdBy: observable,
			comments: observable,
			web: observable,

			displayName: computed,
			addComment: action.bound,
			getCommentById: action.bound,
			deleteComment: action.bound,
			setStatus: action.bound,
			setWeb: action.bound
		})
	}

	/**
	 * Computed display name of the subtask.
	 * @type {string}
	 */
	get displayName() {
		return this.name
	}

	/**
	 * Updates the subtask's name.
	 * @param {string} name - New name.
	 */
	setName(name) {
		this.name = name
	}

	/**
	 * Adds a new comment to this subtask.
	 * @param {string} description - Comment text.
	 * @param {Array<string>} images - Attached image filenames.
	 * @param {string} createdBy - User ID of commenter.
	 */
	addComment(description, images, createdBy) {
		this.comments.addComment(description, images, createdBy)
	}

	/**
	 * Retrieves a comment by its ID.
	 * @param {string} commentId - Identifier of the comment.
	 * @returns {import('../comments').Comment|undefined}
	 */
	getCommentById(commentId) {
		return this.comments.getCommentById(commentId)
	}

	/**
	 * Removes a comment by its ID.
	 * @param {string} commentId - Identifier of the comment.
	 */
	deleteComment(commentId) {
		this.comments.deleteComment(commentId)
	}

	/**
	 * Updates the status of this subtask.
	 * @param {string} status - New status value.
	 */
	setStatus(status) {
		this.status = status
	}

	/**
	 * Always returns 'base' for this class.
	 * @type {string}
	 */
	get subtaskType() {
		return 'base'
	}

	/**
	 * Updates the related web link.
	 * @param {string} web - New URL.
	 */
	setWeb(web) {
		this.web = web
	}
}

/**
 * Class representing a collection of Subtasks.
 */
/**
 * Subtasks manages a collection of Subtask instances.
 *
 * @class Subtasks
 * @param {Array<Object>} [list=[]] - Array of raw subtask data objects.
 */
export class Subtasks {
	list = []

	constructor(list = []) {
		this.list = list.map((subtask) => new this.usedItemClass(subtask))
		makeObservable(this, {
			list: observable,
			addSubtask: action.bound,
			deleteSubtask: action.bound,
			getSubtaskById: action.bound,
			resetStatus: action.bound,
			sortedList: computed,
			subtaskCount: computed
		})
	}

	/**
	 * Returns the class used to instantiate items in this collection.
	 * @type {Function}
	 */
	get usedItemClass() {
		return Subtask
	}

	/**
	 * Returns subtasks sorted by sortId ascending.
	 * @type {Array<Subtask>}
	 */
	get sortedList() {
		return [...this.list].sort((a, b) => a.sortId - b.sortId)
	}

	/**
	 * Prepares a plain array of subtasks for saving.
	 * Preserves comments arrays.
	 * @returns {Array<Object>}
	 */
	prepareForSave() {
		if (!Array.isArray(this.list)) {
			return [] // Ensure the function returns an empty array if this.list is not an array
		}

		return this.list.map((subtask) => {
			// Ensure subtask.comments.list is an array before mapping
			const comments = Array.isArray(subtask.comments.list)
				? subtask.comments.list.map((comment) => comment)
				: [] // Return an empty array if subtask.comments.list is not an array

			return {
				...subtask,
				comments
			}
		})
	}

	/**
	 * Replaces or adds the provided subtask instance.
	 * @param {Subtask} subtask
	 */
	updateSubtask(subtask) {
		const index = this.list.findIndex((s) => s.id === subtask.id)
		if (index >= 0) {
			this.list[index] = subtask
		}
	}

	/**
	 * Creates and appends a new subtask.
	 *
	 * @param {string} name - Subtask name.
	 * @param {number} hours - Hours (for timed subtasks).
	 * @param {*} sprintsIds - Related sprint IDs (timed only).
	 * @param {Array<string>} images - Image filenames.
	 * @param {string} description - Description text.
	 * @param {string} status - Initial status.
	 * @param {string} web - Related URL.
	 * @param {number} sortId - Ordering index.
	 * @param {string} createdBy - Creator user ID.
	 */
	addSubtask(
		name,
		hours,
		sprintsIds,
		images,
		description,
		status,
		web,
		sortId,
		createdBy
	) {
		const subtask = new Subtask({
			name,
			images,
			description,
			status,
			web,
			sortId,
			createdBy
		})
		this.list.push(subtask)
	}

	/**
	 * Removes a subtask by its ID.
	 * @param {string} subtaskId
	 */
	deleteSubtask(subtaskId) {
		this.list = this.list.filter((subtask) => subtask.id !== subtaskId)
	}

	/**
	 * Finds a subtask by its ID.
	 * @param {string} subtaskId
	 * @returns {Subtask|undefined}
	 */
	getSubtaskById(subtaskId) {
		return this.list.find((subtask) => subtask.id === subtaskId)
	}

	/**
	 * Resets all subtasks' status back to 'active'.
	 */
	resetStatus() {
		this.list.forEach((subtask) => subtask.setStatus('active'))
	}

	/**
	 * Total count of subtasks in the collection.
	 * @type {number}
	 */
	get subtaskCount() {
		return this.list.length
	}

	/**
	 * Always returns 'base' for this collection.
	 * @type {string}
	 */
	get subtaskType() {
		return 'base'
	}
}
